import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
import { useProgress } from '../../context/ProgressContext';
import {
  getCourseById,
  completeLectureApi,
  submitLessonQuizApi,
  submitFinalQuizApi,
  getMyEnrollmentsApi,
  API_HOST
} from '../../api/apiServices';
import ProgressBar from '../../components/base/ProgressBar';

// Resolves a slide image URL against this app's own API host instead of a
// hardcoded localhost address, so slides load correctly wherever the
// backend actually runs (localhost, staging, or production). Also leaves
// blob:/data: URLs (a locally-selected preview that hasn't been saved to
// the server yet) untouched instead of mangling them.
const resolveSlideImageSrc = (url) =>
  (!url || /^(https?:|blob:|data:)/.test(url)) ? url : `${API_HOST}/${url}`;
import { ScoreBadge } from '../../components/base/Badge';

/**
 * SlideViewer Component with smooth animated sliding carousel,
 * keyboard controls, fullscreen mode, autoplay, and zoomable images.
 */
function SlideViewer({ slides = [] }) {
  const [current, setCurrent] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoplay, setIsAutoplay] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const containerRef = useRef(null);

  // Keyboard navigation for slides
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        setCurrent(prev => Math.min(slides.length - 1, prev + 1));
      } else if (e.key === 'ArrowLeft') {
        setCurrent(prev => Math.max(0, prev - 1));
      } else if (e.key === 'Escape') {
        setIsFullscreen(false);
        setZoomedImage(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  // Autoplay timer
  useEffect(() => {
    let timer;
    if (isAutoplay && current < slides.length - 1) {
      timer = setTimeout(() => {
        setCurrent(c => c + 1);
      }, 4000);
    } else if (isAutoplay && current === slides.length - 1) {
      setIsAutoplay(false);
    }
    return () => clearTimeout(timer);
  }, [isAutoplay, current, slides.length]);

  if (!slides || slides.length === 0) {
    return (
      <div className="bg-slate-100 rounded-3xl p-10 text-center text-slate-500">
        No slide deck available for this lecture.
      </div>
    );
  }

  const slide = slides[current] || {};

  return (
    <div 
      ref={containerRef}
      className={`bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden shadow-xl transition-all duration-300 ${
        isFullscreen ? 'fixed inset-0 z-[100] rounded-none flex flex-col justify-between' : 'relative'
      }`}
    >
      {/* Top Toolbar */}
      <div className="flex items-center justify-between px-6 py-3.5 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 text-white z-10">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-teal-400 animate-pulse" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
            Slide Deck
          </span>
          <span className="text-xs text-slate-400 bg-slate-800 px-2.5 py-0.5 rounded-full font-mono">
            {current + 1} / {slides.length}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Autoplay toggle */}
          <button
            type="button"
            onClick={() => setIsAutoplay(!isAutoplay)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 cursor-pointer ${
              isAutoplay 
                ? 'bg-teal-500 text-white' 
                : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
            }`}
            title={isAutoplay ? 'Pause auto-advance' : 'Auto-advance slides'}
          >
            <i className={isAutoplay ? 'ri-pause-line' : 'ri-play-line'} />
            <span className="hidden sm:inline">{isAutoplay ? 'Autoplay On' : 'Autoplay'}</span>
          </button>

          {/* Fullscreen toggle */}
          <button
            type="button"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-colors cursor-pointer"
            title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Presentation'}
          >
            <i className={isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} />
          </button>
        </div>
      </div>

      {/* Slide Carousel Stage */}
      <div className="relative overflow-hidden w-full min-h-[380px] md:min-h-[460px] flex items-center bg-gradient-to-b from-slate-950 to-slate-900">
        {/* Track containing all slides */}
        <div 
          className="flex w-full h-full transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {slides.map((s, idx) => (
            <div
              key={s.id || idx}
              className="w-full flex-shrink-0 flex items-center justify-center p-6 md:p-10 select-none"
            >
              {s.imageUrl ? (
                // A slide with an image shows only the image, at full size
                // in the stage -- no extracted-text panel next to it.
                <div className="w-full h-full flex items-center justify-center relative group">
                  <div className="relative rounded-2xl overflow-hidden border border-slate-700/80 shadow-2xl bg-black max-h-[420px] w-full flex items-center justify-center">
                    <img
                      src={resolveSlideImageSrc(s.imageUrl)}
                      alt={s.title || `Slide ${idx + 1}`}
                      className="w-full h-full max-h-[420px] object-contain cursor-zoom-in group-hover:scale-[1.02] transition-transform duration-300"
                      onClick={() => setZoomedImage(s.imageUrl)}
                    />
                    <div
                      onClick={() => setZoomedImage(s.imageUrl)}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-medium gap-1.5 cursor-pointer backdrop-blur-xs"
                    >
                      <i className="ri-zoom-in-line text-lg" /> Click to expand image
                    </div>
                  </div>
                </div>
              ) : (
                // Text-only slide (no image at all): keep the centered
                // title/content treatment.
                <div className="w-full max-w-2xl text-center text-white space-y-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/20 border border-teal-400/30 rounded-full text-teal-300 text-xs font-semibold">
                    <i className="ri-slideshow-line" /> Step {idx + 1}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-extrabold text-white leading-snug tracking-tight">
                    {s.title || `Key Concept ${idx + 1}`}
                  </h3>
                  {s.content && (
                    <div className="text-slate-300 text-sm md:text-base leading-relaxed whitespace-pre-line bg-slate-950/60 p-5 rounded-2xl border border-slate-800/80 font-normal">
                      {s.content}
                    </div>
                  )}
                  {s.caption && !s.content && (
                    <p className="text-slate-300 text-base leading-relaxed">{s.caption}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Floating Prev / Next Arrow buttons on Stage */}
        <button
          type="button"
          onClick={() => setCurrent(c => Math.max(0, c - 1))}
          disabled={current === 0}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-slate-900/80 hover:bg-teal-600 text-white border border-slate-700 flex items-center justify-center transition-all disabled:opacity-20 disabled:hover:bg-slate-900 cursor-pointer shadow-lg z-10"
          aria-label="Previous Slide"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>

        <button
          type="button"
          onClick={() => setCurrent(c => Math.min(slides.length - 1, c + 1))}
          disabled={current === slides.length - 1}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-slate-900/80 hover:bg-teal-600 text-white border border-slate-700 flex items-center justify-center transition-all disabled:opacity-20 disabled:hover:bg-slate-900 cursor-pointer shadow-lg z-10"
          aria-label="Next Slide"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      {/* Bottom Controls & Slide Thumbnails */}
      <div className="p-4 bg-slate-950/90 border-t border-slate-800 flex flex-wrap items-center justify-between gap-4">
        {/* Previous Button */}
        <button 
          type="button"
          onClick={() => setCurrent(c => Math.max(0, c - 1))} 
          disabled={current === 0}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800 text-white text-xs font-semibold disabled:opacity-30 hover:bg-slate-700 transition cursor-pointer"
        >
          <i className="ri-arrow-left-line" /> Previous Slide
        </button>

        {/* Dot Indicators */}
        <div className="flex items-center gap-2 max-w-xs overflow-x-auto py-1 px-2">
          {slides.map((_, dotIdx) => (
            <button
              key={dotIdx}
              type="button"
              onClick={() => setCurrent(dotIdx)}
              className={`h-2.5 rounded-full transition-all cursor-pointer ${
                current === dotIdx 
                  ? 'w-7 bg-teal-400 shadow-sm shadow-teal-400/50' 
                  : 'w-2.5 bg-slate-700 hover:bg-slate-500'
              }`}
              title={`Jump to slide ${dotIdx + 1}`}
            />
          ))}
        </div>

        {/* Next Button */}
        <button 
          type="button"
          onClick={() => setCurrent(c => Math.min(slides.length - 1, c + 1))} 
          disabled={current === slides.length - 1}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-500 text-white text-xs font-semibold disabled:opacity-30 disabled:hover:bg-teal-600 transition cursor-pointer shadow-md shadow-teal-900/40"
        >
          Next Slide <i className="ri-arrow-right-line" />
        </button>
      </div>

      {/* Fullscreen Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button 
            onClick={() => setZoomedImage(null)} 
            className="absolute top-4 right-4 text-white text-3xl hover:text-rose-400"
          >
            <i className="ri-close-line" />
          </button>
          <img 
            src={resolveSlideImageSrc(zoomedImage)}
            alt="Expanded Slide" 
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl border border-slate-700" 
          />
        </div>
      )}
    </div>
  );
}

/**
 * QuizSection Component with interactive MCQ, True/False,
 * Practical Task Assignment submission, and Image Upload attachments.
 */
function QuizSection({ 
  questions = [], 
  quizId, 
  courseId, 
  lessonId, 
  isFinal = false, 
  onComplete,
  onCancel,
  hasNextLesson = false,
  onProceedNext = null,
  onBackToMaterial = () => {}
}) {
  const [answers, setAnswers] = useState({});
  const [taskImages, setTaskImages] = useState({});
  const [taskNotes, setTaskNotes] = useState({});
  const [discussionNotes, setDiscussionNotes] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  // Set once grading comes back from the server (see handleSubmit) --
  // {score, passed, passingScore, correctCount, totalQuestions, breakdown}.
  // The results view below reads correctness/correct-answer data from this,
  // never from the (now answer-free) `questions` prop -- see quizGrading.js
  // on the backend for why the quiz payload no longer carries answers.
  const [gradingResult, setGradingResult] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const currentQuestion = questions[currentQ];
  const questionKey = currentQuestion ? (currentQuestion.id || currentQuestion._id || `q-${currentQ}`) : `q-${currentQ}`;
  const isTaskType = currentQuestion?.type === 'task';
  const isDiscussionType = currentQuestion?.type === 'discussion';
  const isMultiSelectType = currentQuestion?.type === 'multi-select';
  const isFillBlankType = currentQuestion?.type === 'fill-blank';
  const isOrderingType = currentQuestion?.type === 'ordering';

  const handleAnswer = (qKey, idx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qKey]: idx }));
  };

  const handleMultiToggle = (qKey, idx) => {
    if (submitted) return;
    setAnswers(prev => {
      const current = Array.isArray(prev[qKey]) ? prev[qKey] : [];
      const next = current.includes(idx)
        ? current.filter(i => i !== idx)
        : [...current, idx].sort((a, b) => a - b);
      return { ...prev, [qKey]: next };
    });
  };

  const handleFillBlankChange = (qKey, value) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [qKey]: value }));
  };

  // 'ordering' questions arrive with their options already shuffled by the
  // server (see backend/utils/quizGrading.js's sanitizeQuestionForStudent --
  // the true authored order IS the answer for this type, so it can't be
  // shuffled client-side from a copy the client can already read). All that's
  // needed here is to seed the student's arrangement to "as shown" the first
  // time this question is viewed; moveOrderingAnswer below rearranges it.
  useEffect(() => {
    if (currentQuestion?.type === 'ordering' && !Array.isArray(answers[questionKey])) {
      const identityOrder = (currentQuestion.options || []).map((_, i) => i);
      setAnswers(prev => (Array.isArray(prev[questionKey]) ? prev : { ...prev, [questionKey]: identityOrder }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey, currentQuestion?.type]);

  const moveOrderingAnswer = (qKey, idx, dir) => {
    if (submitted) return;
    setAnswers(prev => {
      const current = [...(Array.isArray(prev[qKey]) ? prev[qKey] : [])];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= current.length) return prev;
      [current[idx], current[newIdx]] = [current[newIdx], current[idx]];
      return { ...prev, [qKey]: current };
    });
  };

  const handleTaskImageUpload = (qKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      alert('Image file size must be under 8MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setTaskImages(prev => ({ ...prev, [qKey]: reader.result }));
      // Automatically record as answered when an image or task proof is attached
      setAnswers(prev => ({ ...prev, [qKey]: 0 }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveTaskImage = (qKey) => {
    setTaskImages(prev => {
      const copy = { ...prev };
      delete copy[qKey];
      return copy;
    });
  };

  const isQuestionAnswered = (qKey, q) => {
    if (q.type === 'task') {
      return !!taskImages[qKey] || !!taskNotes[qKey]?.trim() || answers[qKey] !== undefined;
    }
    // Discussion questions are open-ended and ungraded -- a personal
    // reflection is welcome but never required to move on.
    if (q.type === 'discussion') {
      return true;
    }
    if (q.type === 'multi-select') {
      return Array.isArray(answers[qKey]) && answers[qKey].length > 0;
    }
    if (q.type === 'fill-blank') {
      return !!(answers[qKey] || '').trim();
    }
    if (q.type === 'ordering') {
      return Array.isArray(answers[qKey]);
    }
    return answers[qKey] !== undefined;
  };

  const allQuestionsAnswered = questions.every((q, idx) => {
    const k = q.id || q._id || `q-${idx}`;
    return isQuestionAnswered(k, q);
  });

  // Builds the {questionId: studentAnswer} payload the server grades
  // against its own stored quiz -- see backend/utils/quizGrading.js. Keyed
  // by each question's real _id (not the local `qKey`, which falls back to
  // a positional id for questions that somehow lack one) since that's what
  // the server looks answers up by.
  const buildAnswersPayload = () => {
    const payload = {};
    questions.forEach((q, idx) => {
      const localKey = q.id || q._id || `q-${idx}`;
      const serverKey = String(q._id || localKey);
      if (q.type === 'ordering') {
        // Submitted as the option TEXT in the order the student placed it
        // (not positions) -- the server compares this directly against its
        // own true-order array, so it never needs to know how the options
        // were shuffled for display.
        const order = Array.isArray(answers[localKey]) ? answers[localKey] : [];
        payload[serverKey] = order.map(displayIdx => q.options?.[displayIdx]).filter(v => v !== undefined);
      } else if (q.type === 'task') {
        payload[serverKey] = taskNotes[localKey] || (taskImages[localKey] ? 'image-attached' : true);
      } else if (q.type === 'discussion') {
        payload[serverKey] = discussionNotes[localKey] || 'reflected';
      } else {
        payload[serverKey] = answers[localKey];
      }
    });
    return payload;
  };

  const handleSubmit = async () => {
    if (!allQuestionsAnswered && questions.length > 0) {
      alert("Please answer all questions and complete any practical tasks before submitting.");
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const answersPayload = buildAnswersPayload();
      const apiResult = isFinal
        ? await submitFinalQuizApi(courseId, { answers: answersPayload })
        : await submitLessonQuizApi(courseId, lessonId, { answers: answersPayload });

      if (!apiResult?.success || !apiResult?.result) {
        throw new Error(apiResult?.message || 'The server could not grade this submission.');
      }

      const { score, passed, passingScore, correctCount, totalQuestions } = apiResult.result;
      setGradingResult(apiResult.result);
      setSubmitted(true);

      onComplete({
        quizId: quizId || `quiz-${lessonId || courseId}`,
        lessonId,
        courseId,
        score,
        passingScore,
        totalQuestions,
        correctAnswers: correctCount,
        passed,
        completedAt: new Date().toISOString(),
        isFinalQuiz: isFinal,
        taskImages,
        taskNotes,
        discussionNotes,
      });
    } catch (err) {
      console.error("Failed to submit quiz:", err);
      // Grading now happens on the server, so there's no honest local score
      // to fall back to -- surface the error and let the student retry
      // rather than silently marking an ungraded attempt as passed.
      setSubmitError(
        err.response?.data?.message ||
        err.message ||
        "Couldn't submit your answers -- check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (questions.length === 0) {
    return (
      <div className="bg-white rounded-3xl border border-slate-100 p-10 text-center">
        <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-teal-600 text-2xl">
          <i className="ri-checkbox-circle-line" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">No Quiz for this Lesson</h3>
        <p className="text-slate-500 mb-6 text-sm">You can proceed directly to mark this lesson as completed.</p>
        <button
          onClick={() => onComplete({ score: 100, passed: true, isFinalQuiz: isFinal })}
          className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700"
        >
          Confirm & Complete Lesson
        </button>
      </div>
    );
  }

  // Submitted / Results View
  if (submitted) {
    if (!gradingResult) {
      // Shouldn't normally happen -- submitted is only ever set true in the
      // same handler that just set gradingResult -- but guards against a
      // stray render landing in between the two state updates.
      return null;
    }

    const { score, passed, passingScore, correctCount, totalQuestions, breakdown = [] } = gradingResult;
    // The server's per-question grading breakdown, keyed by question id, so
    // the reveal below reads correctness/correct-answer info from the
    // server's verdict instead of the (now answer-free) sanitized question
    // props.
    const breakdownByQ = {};
    breakdown.forEach((entry) => {
      breakdownByQ[String(entry.questionId)] = entry;
    });

    const resetForRetake = () => {
      setSubmitted(false);
      setGradingResult(null);
      setSubmitError('');
      setAnswers({});
      setTaskNotes({});
      setTaskImages({});
      setDiscussionNotes({});
      setCurrentQ(0);
    };

    return (
      <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10 animate-fade-in">
        <div className="text-center mb-8 pb-8 border-b border-slate-100">
          <div className="w-20 h-20 mx-auto rounded-3xl bg-teal-50 flex items-center justify-center mb-4">
            <ScoreBadge score={score} />
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {passed ? "Checkpoint Passed!" : "Checkpoint Not Passed"}
          </h2>
          <p className="text-slate-600 text-sm mb-4">
            You scored <strong className="text-slate-900">{score}%</strong> ({correctCount} of {totalQuestions} items completed).
            {passed ? (
              <span className="text-emerald-700 font-semibold block mt-1">
                ✓ Great job! You have passed this lesson checkpoint. The next lesson is now unlocked.
              </span>
            ) : (
              <span className="text-rose-600 font-semibold block mt-1">
                You need at least {passingScore}% to pass this checkpoint before moving to the next lesson.
              </span>
            )}
          </p>

          {/* Action buttons directly in Quiz Results View */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
            {passed ? (
              <>
                {hasNextLesson && onProceedNext ? (
                  <button
                    type="button"
                    onClick={onProceedNext}
                    className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs sm:text-sm rounded-xl transition-all shadow-md inline-flex items-center gap-2 cursor-pointer"
                  >
                    Proceed to Next Lesson <i className="ri-arrow-right-line text-base" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={onBackToMaterial}
                    className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl transition-all shadow-md inline-flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-checkbox-circle-fill text-base" /> Lesson Completed
                  </button>
                )}
                <button
                  type="button"
                  onClick={onBackToMaterial}
                  className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-all cursor-pointer inline-flex items-center gap-1.5"
                >
                  <i className="ri-book-open-line text-sm" /> Review Lesson Material
                </button>
                <button
                  type="button"
                  onClick={resetForRetake}
                  className="px-4 py-3 bg-transparent hover:bg-slate-100 text-slate-500 font-medium text-xs rounded-xl transition-all cursor-pointer inline-flex items-center gap-1"
                >
                  <i className="ri-refresh-line text-sm" /> Retake
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={resetForRetake}
                  className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-bold text-xs rounded-xl transition-all shadow-md inline-flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-refresh-line text-sm" /> Retake Quiz Checkpoint
                </button>
                <button
                  type="button"
                  onClick={onBackToMaterial}
                  className="px-5 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs rounded-xl transition-all cursor-pointer inline-flex items-center gap-1.5"
                >
                  <i className="ri-book-open-line text-sm" /> Study Lesson Material
                </button>
              </>
            )}
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="space-y-6">
          {questions.map((q, idx) => {
            const k = q.id || q._id || `q-${idx}`;
            const serverKey = String(q._id || k);
            const entry = breakdownByQ[serverKey] || {};
            const isTask = q.type === 'task';
            const isDiscussion = q.type === 'discussion';
            const isMultiSelect = q.type === 'multi-select';
            const isFillBlank = q.type === 'fill-blank';
            const isOrdering = q.type === 'ordering';
            const userAnswer = answers[k];
            const isCorrect = !!entry.correct;

            return (
              <div 
                key={idx} 
                className={`p-6 rounded-2xl border transition-all ${
                  isCorrect 
                    ? 'bg-emerald-50/60 border-emerald-200' 
                    : 'bg-rose-50/60 border-rose-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                      isCorrect ? 'bg-emerald-500' : 'bg-rose-500'
                    }`}>
                      {isCorrect ? '✓' : '✗'}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {isTask ? 'Practical Task' : isDiscussion ? 'Discussion Question' : `Question ${idx + 1}`}
                    </span>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    isCorrect ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                  }`}>
                    {isCorrect ? 'Completed' : 'Incorrect'}
                  </span>
                </div>

                <p className="font-semibold text-slate-900 mb-3">{q.text || q.questionText}</p>

                {isDiscussion && (
                  <div className="mb-3 bg-white p-3 rounded-xl border border-slate-200 text-xs text-slate-700">
                    <p className="font-semibold text-slate-500 mb-1">Your Reflection:</p>
                    <p className="whitespace-pre-wrap">{discussionNotes[k]?.trim() || 'No reflection shared -- that\'s okay, this one is just for you to think about or discuss in class.'}</p>
                  </div>
                )}

                {/* If user uploaded an image task response */}
                {taskImages[k] && (
                  <div className="mb-4 bg-white p-3 rounded-xl border border-slate-200 max-w-sm">
                    <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                      <i className="ri-attachment-line text-teal-600" /> Your Uploaded Task Image:
                    </p>
                    <img 
                      src={taskImages[k]} 
                      alt="Submitted work" 
                      className="w-full h-36 object-contain rounded-lg border bg-slate-50 cursor-pointer"
                      onClick={() => setZoomedImage(taskImages[k])}
                    />
                  </div>
                )}

                {taskNotes[k] && (
                  <div className="mb-3 bg-white p-3 rounded-xl border border-slate-200 text-xs text-slate-700">
                    <p className="font-semibold text-slate-500 mb-1">Your Solution Notes:</p>
                    <p className="whitespace-pre-wrap">{taskNotes[k]}</p>
                  </div>
                )}

                {!isTask && !isDiscussion && !isMultiSelect && !isFillBlank && !isOrdering && (
                  <div className="text-sm space-y-1 mt-2">
                    <div className="text-slate-700">
                      <strong>Your Answer:</strong> {q.options?.[userAnswer] || 'Not answered'}
                    </div>
                    {!isCorrect && (
                      <div className="text-emerald-700 font-medium">
                        <strong>Correct Answer:</strong> {q.options?.[entry.correctAnswer ?? 0]}
                      </div>
                    )}
                  </div>
                )}

                {isMultiSelect && (
                  <div className="text-sm space-y-1 mt-2">
                    <div className="text-slate-700">
                      <strong>Your Answer:</strong> {Array.isArray(userAnswer) && userAnswer.length > 0
                        ? userAnswer.map(i => q.options?.[i]).filter(Boolean).join(', ')
                        : 'Not answered'}
                    </div>
                    {!isCorrect && (
                      <div className="text-emerald-700 font-medium">
                        <strong>Correct Answer:</strong> {(entry.correctAnswers || []).map(i => q.options?.[i]).filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {isFillBlank && (
                  <div className="text-sm space-y-1 mt-2">
                    <div className="text-slate-700">
                      <strong>Your Answer:</strong> {userAnswer?.trim() || 'Not answered'}
                    </div>
                    {!isCorrect && (
                      <div className="text-emerald-700 font-medium">
                        <strong>Accepted Answer{entry.acceptedAnswers?.length > 1 ? 's' : ''}:</strong> {(entry.acceptedAnswers || []).join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {isOrdering && (
                  <div className="text-sm space-y-1 mt-2">
                    <div className="text-slate-700">
                      <strong>Your Order:</strong> {Array.isArray(userAnswer) && userAnswer.length > 0
                        ? userAnswer.map(i => q.options?.[i]).filter(Boolean).join(' → ')
                        : 'Not answered'}
                    </div>
                    {!isCorrect && (
                      <div className="text-emerald-700 font-medium">
                        <strong>Correct Order:</strong> {(entry.correctOrder || []).join(' → ')}
                      </div>
                    )}
                  </div>
                )}

                {entry.explanation && (
                  <p className="text-slate-600 text-xs mt-3 pt-3 border-t border-slate-200/80 leading-relaxed">
                    <strong>{isDiscussion ? "Facilitator's Note:" : 'Explanation:'}</strong> {entry.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-teal-500" />
            <h3 className="text-xl font-bold text-slate-900">
              {isFinal ? 'Final Course Assessment' : 'Lesson Checkpoint & Task'}
            </h3>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Complete the questions and task exercises to master this lecture.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Back to Lesson
            </button>
          )}
          <span className="px-3.5 py-1 bg-slate-100 rounded-full text-xs font-bold text-slate-700">
            {currentQ + 1} of {questions.length}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <ProgressBar 
        value={((currentQ + 1) / questions.length) * 100} 
        size="sm" 
        className="mb-8" 
      />

      {/* Question Item Container */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${
            isTaskType
              ? 'bg-amber-100 text-amber-800 border border-amber-200'
              : isDiscussionType
              ? 'bg-purple-100 text-purple-800 border border-purple-200'
              : isMultiSelectType
              ? 'bg-sky-100 text-sky-800 border border-sky-200'
              : isFillBlankType
              ? 'bg-lime-100 text-lime-800 border border-lime-200'
              : isOrderingType
              ? 'bg-orange-100 text-orange-800 border border-orange-200'
              : currentQuestion?.type === 'true-false'
              ? 'bg-indigo-100 text-indigo-800'
              : 'bg-teal-100 text-teal-800'
          }`}>
            <i className={
              isTaskType ? 'ri-task-line' :
              isDiscussionType ? 'ri-discuss-line' :
              isMultiSelectType ? 'ri-checkbox-multiple-line' :
              isFillBlankType ? 'ri-input-method-line' :
              isOrderingType ? 'ri-sort-asc' :
              'ri-questionnaire-line'
            } />
            {isTaskType ? 'PRACTICAL TASK ASSIGNMENT' :
              isDiscussionType ? 'DISCUSSION QUESTION' :
              isMultiSelectType ? 'MULTI-SELECT (CHOOSE ALL THAT APPLY)' :
              isFillBlankType ? 'FILL IN THE BLANK' :
              isOrderingType ? 'PUT IN ORDER' :
              currentQuestion?.type === 'true-false' ? 'TRUE / FALSE' : 'MULTIPLE CHOICE'}
          </span>
        </div>

        <h4 className="text-lg md:text-xl font-bold text-slate-900 leading-snug mb-4">
          {currentQuestion?.text || currentQuestion?.questionText}
        </h4>

        {/* Optional Prompt Image Attached to the Question */}
        {currentQuestion?.imageUrl && (
          <div className="mb-6 rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 max-h-72 flex items-center justify-center p-2 relative group">
            <img 
              src={currentQuestion.imageUrl} 
              alt="Question Diagram" 
              className="max-h-64 object-contain rounded-lg cursor-zoom-in group-hover:scale-[1.01] transition-transform"
              onClick={() => setZoomedImage(currentQuestion.imageUrl)}
            />
            <div 
              onClick={() => setZoomedImage(currentQuestion.imageUrl)}
              className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2.5 py-1 rounded-lg flex items-center gap-1 backdrop-blur-xs cursor-pointer"
            >
              <i className="ri-zoom-in-line" /> Zoom diagram
            </div>
          </div>
        )}

        {/* Practical Task Mode: Task Submission & Image Uploader */}
        {isTaskType ? (
          <div className="space-y-5 my-6 p-5 bg-teal-50/50 rounded-2xl border border-teal-100">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                📸 Upload Screenshot / Solution Proof *
              </label>
              
              {taskImages[questionKey] ? (
                <div className="relative rounded-2xl overflow-hidden border border-teal-200 bg-white p-3 max-w-md shadow-sm">
                  <img 
                    src={taskImages[questionKey]} 
                    alt="Task preview" 
                    className="w-full h-44 object-contain rounded-xl bg-slate-50 border cursor-pointer"
                    onClick={() => setZoomedImage(taskImages[questionKey])}
                  />
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill text-emerald-600" /> Image attached successfully
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTaskImage(questionKey)}
                      className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1"
                    >
                      <i className="ri-delete-bin-line" /> Remove / Replace
                    </button>
                  </div>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-teal-300 hover:border-teal-500 rounded-2xl cursor-pointer bg-white hover:bg-teal-50/40 transition-all group">
                  <div className="w-12 h-12 rounded-2xl bg-teal-100 text-teal-600 flex items-center justify-center text-2xl mb-3 group-hover:scale-110 transition-transform">
                    <i className="ri-upload-cloud-2-line" />
                  </div>
                  <span className="text-sm font-bold text-slate-800 mb-1">
                    Click to browse or drag & drop task image
                  </span>
                  <span className="text-xs text-slate-500">
                    Upload your finished screenshot, diagram, or photo (PNG, JPG, WebP)
                  </span>
                  <input 
                    type="file" 
                    accept="image/*" 
                    onChange={(e) => handleTaskImageUpload(questionKey, e)} 
                    className="hidden" 
                  />
                </label>
              )}
            </div>

            {/* Task Explanation / Code / Notes input */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                ✍️ Solution Notes, Code Snippet, or Summary
              </label>
              <textarea
                value={taskNotes[questionKey] || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setTaskNotes(prev => ({ ...prev, [questionKey]: val }));
                  setAnswers(prev => ({ ...prev, [questionKey]: 0 }));
                }}
                placeholder="Explain what you built or paste relevant code excerpts..."
                rows={3}
                className="w-full p-3.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-none font-sans"
              />
            </div>
          </div>
        ) : isDiscussionType ? (
          /* Open-ended discussion: an optional personal reflection box.
             Nothing here is graded or required -- it's meant to be
             discussed in class or thought about privately. */
          <div className="space-y-3 my-6 p-5 bg-purple-50/50 rounded-2xl border border-purple-100">
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              💬 Your Reflection (Optional)
            </label>
            <textarea
              value={discussionNotes[questionKey] || ''}
              onChange={(e) => setDiscussionNotes(prev => ({ ...prev, [questionKey]: e.target.value }))}
              placeholder="Jot down your own thoughts here, or just discuss this one with your group..."
              rows={4}
              className="w-full p-3.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none font-sans"
            />
            <p className="text-xs text-purple-700">
              There's no right or wrong answer -- this one's for reflection and group discussion.
            </p>
          </div>
        ) : isMultiSelectType ? (
          /* Multi-Select: checkboxes, more than one can be correct */
          <div className="space-y-3 mb-8">
            {(currentQuestion?.options || []).map((option, idx) => {
              const isSelected = Array.isArray(answers[questionKey]) && answers[questionKey].includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleMultiToggle(questionKey, idx)}
                  className={`w-full text-left p-4 md:p-5 rounded-2xl border-2 transition-all flex items-center justify-between cursor-pointer ${
                    isSelected
                      ? 'border-sky-500 bg-sky-50/80 text-sky-900 shadow-sm'
                      : 'border-slate-200 hover:border-sky-300 hover:bg-slate-50 text-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-colors ${
                      isSelected ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {isSelected && <i className="ri-check-line text-base" />}
                    </span>
                    <span className="text-base font-medium">{option}</span>
                  </div>
                </button>
              );
            })}
            <p className="text-xs text-slate-400">Select every option you think is correct.</p>
          </div>
        ) : isFillBlankType ? (
          /* Fill in the Blank: free text, matched case-insensitively */
          <div className="my-6">
            <input
              type="text"
              value={answers[questionKey] || ''}
              onChange={(e) => handleFillBlankChange(questionKey, e.target.value)}
              placeholder="Type your answer..."
              className="w-full p-4 md:p-5 bg-white border-2 border-slate-200 focus:border-lime-500 rounded-2xl text-base font-medium focus:outline-none transition-colors"
            />
            <p className="text-xs text-slate-400 mt-2">Not case-sensitive -- close spelling counts if it matches an accepted answer.</p>
          </div>
        ) : isOrderingType ? (
          /* Ordering: rearrange the shuffled items back into the right order */
          <div className="space-y-3 mb-8">
            {(Array.isArray(answers[questionKey]) ? answers[questionKey] : []).map((origIdx, pos) => (
              <div
                key={origIdx}
                className="w-full p-4 md:p-5 rounded-2xl border-2 border-slate-200 bg-white flex items-center justify-between gap-3"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <span className="w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs bg-orange-100 text-orange-700 flex-shrink-0">
                    {pos + 1}
                  </span>
                  <span className="text-base font-medium truncate">{currentQuestion.options?.[origIdx]}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => moveOrderingAnswer(questionKey, pos, -1)}
                    disabled={pos === 0}
                    className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-orange-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-up-line" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveOrderingAnswer(questionKey, pos, 1)}
                    disabled={pos === answers[questionKey].length - 1}
                    className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-orange-600 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <i className="ri-arrow-down-line" />
                  </button>
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-400">Use the arrows to arrange these into the correct order.</p>
          </div>
        ) : (
          /* MCQ or True/False Options */
          <div className="space-y-3 mb-8">
            {(currentQuestion?.options || []).map((option, idx) => {
              const isSelected = answers[questionKey] === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleAnswer(questionKey, idx)}
                  className={`w-full text-left p-4 md:p-5 rounded-2xl border-2 transition-all flex items-center justify-between cursor-pointer ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50/80 text-teal-900 shadow-sm'
                      : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50 text-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <span className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs font-mono transition-colors ${
                      isSelected ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    <span className="text-base font-medium">{option}</span>
                  </div>
                  {isSelected && (
                    <i className="ri-check-line text-teal-600 text-xl font-bold" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Submission error, if the last attempt failed to reach/grade on the server */}
      {submitError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-start gap-2">
          <i className="ri-error-warning-line text-base mt-0.5" />
          <span>{submitError}</span>
        </div>
      )}

      {/* Navigation & Submit Bar */}
      <div className="flex items-center justify-between pt-6 border-t border-slate-100">
        <button
          type="button"
          onClick={() => setCurrentQ(c => Math.max(0, c - 1))}
          disabled={currentQ === 0}
          className="px-6 py-2.5 text-xs font-bold border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white transition cursor-pointer"
        >
          ← Previous
        </button>

        <div className="flex items-center gap-3">
          {currentQ < questions.length - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentQ(c => c + 1)}
              disabled={!isQuestionAnswered(questionKey, currentQuestion)}
              className="px-8 py-3 bg-teal-600 text-white text-xs font-bold rounded-xl disabled:bg-slate-200 disabled:text-slate-400 hover:bg-teal-700 transition cursor-pointer shadow-md shadow-teal-900/20"
            >
              Next Item →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered || submitting}
              className="px-8 py-3 bg-teal-600 text-white text-xs font-bold rounded-xl disabled:bg-slate-200 disabled:text-slate-400 hover:bg-teal-700 transition cursor-pointer shadow-md shadow-teal-900/20 flex items-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill" />
                  Submit Checkpoint
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button 
            onClick={() => setZoomedImage(null)} 
            className="absolute top-4 right-4 text-white text-3xl hover:text-rose-400"
          >
            <i className="ri-close-line" />
          </button>
          <img 
            src={zoomedImage} 
            alt="Expanded preview" 
            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl border border-slate-700" 
          />
        </div>
      )}
    </div>
  );
}

/**
 * Main Lesson Viewer Page fusing lecture materials, slide viewer,
 * checkpoint tasks, and course progression seamlessly.
 */
export default function LessonViewerPage() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const { 
    completeLesson, 
    isLessonCompleted, 
    saveQuizResult, 
    getQuizResult, 
    completeCourse 
  } = useProgress();

  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [activeView, setActiveView] = useState('content'); // 'content' or 'quiz'
  const [showFinalQuiz, setShowFinalQuiz] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [localPassedQuizzes, setLocalPassedQuizzes] = useState({});

  // Reset activeView to content whenever switching lessons
  useEffect(() => {
    setActiveView('content');
  }, [lessonId]);

  // Load course and find current lesson
  useEffect(() => {
    const loadLesson = async () => {
      if (!courseId || !lessonId) return;

      try {
        setLoading(true);

        const courseRes = await getCourseById(courseId);
        if (!courseRes.success || !courseRes.course) {
          navigate(`/courses/${courseId}`);
          return;
        }

        const fetchedCourse = courseRes.course;
        setCourse(fetchedCourse);

        // Find the lecture across all sections
        let foundLesson = null;
        fetchedCourse.sections?.forEach(section => {
          const lecture = section.lectures?.find(l => String(l._id) === String(lessonId) || String(l.id) === String(lessonId));
          if (lecture) foundLesson = lecture;
        });

        // Also check if course has a flat lessons array
        if (!foundLesson && fetchedCourse.lessons) {
          foundLesson = fetchedCourse.lessons.find(l => String(l._id) === String(lessonId) || String(l.id) === String(lessonId));
        }

        if (!foundLesson) {
          navigate(`/courses/${courseId}`);
          return;
        }

        setCurrentLesson(foundLesson);

        // Verify enrollment
        if (isAuthenticated) {
          const enrollRes = await getMyEnrollmentsApi();
          const isUserEnrolled = enrollRes.success && 
            enrollRes.enrollments?.some(e => 
              String(e.courseId?._id || e.courseId) === String(courseId)
            );
          setIsEnrolled(isUserEnrolled);

          if (!isUserEnrolled) {
            navigate(`/courses/${courseId}`, { replace: true });
          }
        } else {
          setIsEnrolled(true);
        }
      } catch (err) {
        console.error("Failed to load lesson:", err);
        navigate(`/courses/${courseId}`);
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [courseId, lessonId, isAuthenticated, navigate]);

  // If current lesson has no quiz, force activeView to 'content'
  useEffect(() => {
    if (currentLesson && !currentLesson.quiz?.questions?.length && activeView === 'quiz') {
      setActiveView('content');
    }
  }, [currentLesson, activeView]);

  if (loading || !course || !currentLesson) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6">
        <div className="w-12 h-12 border-3 border-teal-600 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-slate-600 font-medium text-sm">Loading lesson & curriculum...</p>
      </div>
    );
  }

  // Calculate syllabus stats
  const allLectures = [];
  (course.sections || []).forEach(sec => {
    (sec.lectures || []).forEach(lec => {
      allLectures.push({ ...lec, sectionTitle: sec.title });
    });
  });
  if (allLectures.length === 0 && course.lessons) {
    allLectures.push(...course.lessons);
  }

  const currentLessonIndex = allLectures.findIndex(
    l => String(l._id || l.id) === String(currentLesson._id || currentLesson.id)
  );

  const prevLesson = currentLessonIndex > 0 ? allLectures[currentLessonIndex - 1] : null;
  const nextLesson = currentLessonIndex < allLectures.length - 1 ? allLectures[currentLessonIndex + 1] : null;

  const totalLessons = allLectures.length;
  const completedLessonsCount = allLectures.filter(lec => isLessonCompleted(course._id, lec._id || lec.id)).length;
  const progress = totalLessons > 0 ? Math.round((completedLessonsCount / totalLessons) * 100) : 0;

  const hasQuiz = Boolean(currentLesson.quiz?.questions?.length > 0);
  // The instructor sets this per-quiz (Course.js's quizSchema.passingScore,
  // default 70) -- messaging here follows whatever that quiz actually
  // requires instead of assuming every quiz needs 70%.
  const lessonPassingScore = currentLesson.quiz?.passingScore ?? 70;
  const currentLessonStrId = String(currentLesson._id || currentLesson.id);
  const lessonQuizResult = getQuizResult(currentLessonStrId);
  const isQuizPassed = Boolean(
    hasQuiz 
      ? (localPassedQuizzes[currentLessonStrId] || (lessonQuizResult && lessonQuizResult.passed)) 
      : true
  );
  const isCurrentLessonDone = isLessonCompleted(course._id, currentLessonStrId);
  const canAdvanceToNext = Boolean(hasQuiz ? isQuizPassed : true) || isCurrentLessonDone;

  const hasFinalQuiz = Boolean(course.finalQuiz?.questions?.length > 0);
  const finalPassingScore = course.finalQuiz?.passingScore ?? 70;
  const finalQuizResult = getQuizResult(course.finalQuiz?.id || `final-quiz-${course._id}`);
  const isFinalQuizPassed = Boolean(finalQuizResult?.passed);

  const isAllLessonsCompleted = totalLessons > 0 && allLectures.every(l => {
    const lId = String(l._id || l.id);
    if (lId === currentLessonStrId) {
      return isCurrentLessonDone || isQuizPassed;
    }
    return isLessonCompleted(course._id, lId);
  });

  const isFullyCompletedCourse = isAllLessonsCompleted && (!hasFinalQuiz || isFinalQuizPassed);

  const handleMarkComplete = async () => {
    if (hasQuiz && !isQuizPassed) {
      setActiveView('quiz');
      return;
    }

    try {
      setCompleting(true);
      await completeLectureApi(course._id, currentLessonStrId);
      completeLesson(course._id, currentLessonStrId);
    } catch (err) {
      console.warn("Could not sync complete lecture with backend, saving locally:", err);
      completeLesson(course._id, currentLessonStrId);
    } finally {
      setCompleting(false);
    }
  };

  const handleNextLesson = async () => {
    if (!canAdvanceToNext) {
      setActiveView('quiz');
      return;
    }

    // Auto-mark non-quiz lesson as completed when advancing
    if (!hasQuiz && !isCurrentLessonDone) {
      try {
        await completeLectureApi(course._id, currentLessonStrId);
        completeLesson(course._id, currentLessonStrId);
      } catch (err) {
        completeLesson(course._id, currentLessonStrId);
      }
    }

    if (nextLesson) {
      setActiveView('content');
      navigate(`/courses/${course._id}/lesson/${nextLesson._id || nextLesson.id}`);
    }
  };

  const handleSelectLessonFromSidebar = (targetLesson) => {
    const targetId = targetLesson._id || targetLesson.id;
    const currentId = currentLesson._id || currentLesson.id;

    if (String(targetId) === String(currentId)) return;

    // Check if user is trying to skip ahead past an uncompleted quiz
    if (hasQuiz && !isQuizPassed) {
      const isTargetUpcoming = allLectures.findIndex(l => String(l._id || l.id) === String(targetId)) > currentLessonIndex;
      if (isTargetUpcoming) {
        alert("Please complete and pass the quiz for the current lesson before advancing to the next lesson.");
        setActiveView('quiz');
        return;
      }
    }

    setActiveView('content');
    navigate(`/courses/${course._id}/lesson/${targetId}`);
  };

  const handleQuizComplete = (result) => {
    const currentId = String(currentLesson._id || currentLesson.id);
    if (result.passed) {
      setLocalPassedQuizzes(prev => ({ ...prev, [currentId]: true }));
      completeLesson(course._id, currentId);
    }

    saveQuizResult({
      ...result,
      courseId: course._id,
      lessonId: currentId,
    });

    if (result.isFinalQuiz && result.passed) {
      completeCourse(course._id);
      setTimeout(() => navigate(`/certificate/${course._id}`), 1600);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link 
              to={`/courses/${course._id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-teal-600 transition-colors p-2 hover:bg-slate-100 rounded-xl whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-sm" />
              <span className="hidden sm:inline">Back to Course</span>
            </Link>
            <div className="h-5 w-px bg-slate-200 hidden sm:block" />
            <div className="min-w-0">
              <p className="text-xs text-slate-500 truncate">{course.title}</p>
              <h1 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                {currentLesson.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden md:block text-right">
              <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Progress: {completedLessonsCount}/{totalLessons} ({progress}%)
              </div>
              <div className="w-36 mt-1">
                <ProgressBar value={progress} size="sm" />
              </div>
            </div>

            {/* Quick Next Lecture button */}
            {nextLesson && (
              canAdvanceToNext ? (
                <button
                  onClick={handleNextLesson}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
                >
                  Next <i className="ri-arrow-right-line" />
                </button>
              ) : (
                <button
                  onClick={() => setActiveView('quiz')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
                  title="Complete the quiz to unlock the next lesson"
                >
                  <i className="ri-lock-line" /> Take Quiz
                </button>
              )
            )}
          </div>
        </div>

        {/* Seamless Navigation Tabs */}
        <div className="border-t border-slate-100 bg-slate-50/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
            <div className="flex gap-2 py-2">
              <button
                type="button"
                onClick={() => setActiveView('content')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeView === 'content'
                    ? 'bg-white text-teal-700 shadow-xs border border-slate-200/80'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                }`}
              >
                <i className="ri-book-open-line" />
                Lesson Material
                {(isCurrentLessonDone || isQuizPassed) && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </button>

              {/* Conditional Quiz Button: only render if current lesson has a quiz */}
              {hasQuiz && (
                <button
                  type="button"
                  onClick={() => setActiveView('quiz')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    activeView === 'quiz'
                      ? 'bg-white text-teal-700 shadow-xs border border-slate-200/80'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                  }`}
                >
                  <i className="ri-task-line" />
                  Task & Quiz Checkpoint
                  {isQuizPassed ? (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px]">
                      {lessonQuizResult?.score || 100}% ✓ Passed
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px]">
                      Required
                    </span>
                  )}
                </button>
              )}

              {/* Opens in its own tab so the student doesn't lose their
                  place in this lesson -- the discussion room is scoped to
                  the whole course, not just this lesson. */}
              <Link
                to={`/courses/${course._id}/discussion`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer text-slate-600 hover:text-slate-900 hover:bg-white/60"
              >
                <i className="ri-discuss-line" />
                Discussion
                <i className="ri-external-link-line text-[11px]" />
              </Link>
            </div>

            <div className="text-xs text-slate-500 hidden sm:block">
              {currentLesson.type === 'slides' && 'Interactive Slide Deck'}
              {currentLesson.type === 'video' && 'Video Lecture'}
              {currentLesson.type === 'text' && 'Written Guide'}
              {currentLesson.type === 'resource' && 'Resource'}
              {currentLesson.type === 'live-session' && 'Live Session'}
              {currentLesson.type === 'audio' && 'Audio Lecture'}
            </div>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content Column */}
          <div className="lg:col-span-8 space-y-6">
            {activeView === 'content' && (
              <div className="space-y-6 animate-fade-in">
                {/* 1. Video Lesson */}
                {currentLesson.type === 'video' && currentLesson.videoUrl && (
                  <div className="bg-black rounded-3xl overflow-hidden shadow-lg aspect-video border border-slate-800">
                    <iframe 
                      src={currentLesson.videoUrl} 
                      title={currentLesson.title}
                      className="w-full h-full" 
                      allowFullScreen 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  </div>
                )}

                {/* 2. Slide Lesson */}
                {currentLesson.type === 'slides' && currentLesson.slides?.length > 0 && (
                  <SlideViewer slides={currentLesson.slides} />
                )}

                {/* 3. Text / Markdown Guide */}
                {currentLesson.type === 'text' && currentLesson.textContent && (
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10 prose max-w-none text-slate-800 leading-relaxed">
                    <div className="flex items-center gap-2 text-teal-600 font-bold text-xs uppercase tracking-wider mb-4">
                      <i className="ri-article-line text-base" /> Study Guide & Practical Notes
                    </div>
                    <pre className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed text-base bg-transparent p-0 border-0">
                      {currentLesson.textContent}
                    </pre>
                  </div>
                )}

                {/* 4. Resource / Link Lesson */}
                {currentLesson.type === 'resource' && currentLesson.resourceUrl && (
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-3xl">
                      <i className="ri-file-download-line" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{currentLesson.title}</h3>
                    <p className="text-slate-500 text-sm mb-6">This lesson links to an external resource.</p>
                    <a
                      href={currentLesson.resourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl transition-colors"
                    >
                      <i className="ri-external-link-line" /> {currentLesson.resourceLabel || 'View Resource'}
                    </a>
                  </div>
                )}

                {/* 5. Live Session Lesson */}
                {currentLesson.type === 'live-session' && (
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-3xl">
                      <i className="ri-broadcast-line" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{currentLesson.title}</h3>
                    {currentLesson.scheduledAt && (
                      <p className="text-slate-600 text-sm mb-1 font-semibold">
                        {new Date(currentLesson.scheduledAt).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}
                      </p>
                    )}
                    {currentLesson.sessionPlatform && (
                      <p className="text-slate-400 text-xs mb-4">via {currentLesson.sessionPlatform}</p>
                    )}
                    {currentLesson.sessionUrl ? (
                      <a
                        href={currentLesson.sessionUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold rounded-xl transition-colors mt-2"
                      >
                        <i className="ri-vidicon-line" /> Join Session
                      </a>
                    ) : (
                      <p className="text-slate-400 text-xs mt-4">The join link hasn't been added yet.</p>
                    )}
                  </div>
                )}

                {/* 6. Audio Lesson */}
                {currentLesson.type === 'audio' && currentLesson.audioUrl && (
                  <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 md:p-10">
                    <div className="flex items-center gap-2 text-purple-600 font-bold text-xs uppercase tracking-wider mb-6">
                      <i className="ri-headphone-line text-base" /> Audio Lecture
                    </div>
                    <audio controls className="w-full" src={currentLesson.audioUrl}>
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                )}

                {/* Fused Section: Checkpoint Banner & Action */}
                <div className="bg-gradient-to-r from-teal-900 to-slate-900 text-white rounded-3xl p-6 md:p-8 shadow-md flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div className="space-y-1 text-center sm:text-left">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-500/20 border border-teal-400/30 rounded-full text-teal-300 text-xs font-semibold">
                      <i className="ri-award-line" /> {hasQuiz ? 'Lesson Assessment Gate' : 'Lesson Completion'}
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {hasQuiz 
                        ? (isQuizPassed ? '✓ Lesson Quiz Completed & Passed' : 'Quiz Checkpoint Required')
                        : (isCurrentLessonDone ? '✓ Lesson Completed' : 'Ready to Complete this Lecture?')}
                    </h3>
                    <p className="text-slate-300 text-xs max-w-md">
                      {hasQuiz 
                        ? (isQuizPassed 
                            ? `You achieved ${lessonQuizResult?.score || 100}% and unlocked lesson progression.` 
                            : `You must take and pass the quiz (${lessonPassingScore}%+ required) before advancing to the next lesson.`)
                        : 'Mark this lesson as finished to update your course progress and unlock your certificate.'}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    {hasQuiz ? (
                      <button
                        type="button"
                        onClick={() => setActiveView('quiz')}
                        className={`px-7 py-3.5 font-bold text-xs rounded-xl transition-all shadow-md active:scale-95 whitespace-nowrap text-center cursor-pointer ${
                          isQuizPassed 
                            ? 'bg-emerald-400 hover:bg-emerald-300 text-slate-950' 
                            : 'bg-amber-400 hover:bg-amber-300 text-slate-950'
                        }`}
                      >
                        <i className="ri-pencil-ruler-2-line mr-1.5" />
                        {isQuizPassed ? 'Review Quiz & Answers' : 'Take Lesson Quiz (Required)'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleMarkComplete}
                        disabled={completing || isCurrentLessonDone}
                        className="px-7 py-3.5 bg-teal-500 hover:bg-teal-400 text-white font-bold text-xs rounded-xl transition-all shadow-md active:scale-95 whitespace-nowrap text-center cursor-pointer disabled:opacity-75 disabled:cursor-default"
                      >
                        {isCurrentLessonDone ? '✓ Lesson Completed' : completing ? 'Saving...' : 'Mark as Completed'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Lesson Navigation Footer */}
                <div className="flex items-center justify-between pt-4">
                  {prevLesson ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/courses/${course._id}/lesson/${prevLesson._id || prevLesson.id}`)}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-line" /> Previous: {prevLesson.title}
                    </button>
                  ) : <div />}

                  {nextLesson ? (
                    canAdvanceToNext ? (
                      <button
                        type="button"
                        onClick={handleNextLesson}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                      >
                        Next: {nextLesson.title} <i className="ri-arrow-right-line" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setActiveView('quiz')}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                        title="Pass the quiz to unlock the next lesson"
                      >
                        <i className="ri-lock-line" /> Take Quiz to Unlock Next <i className="ri-arrow-right-line" />
                      </button>
                    )
                  ) : (
                    /* Last Lesson Actions - Restrict manual false completion */
                    isFullyCompletedCourse ? (
                      <Link
                        to={`/certificate/${course._id}`}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                      >
                        Course Completed! View Certificate <i className="ri-award-line" />
                      </Link>
                    ) : isAllLessonsCompleted && hasFinalQuiz ? (
                      <Link
                        to={`/courses/${course._id}`}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-xl transition-colors shadow-sm cursor-pointer"
                      >
                        Take Final Course Quiz <i className="ri-trophy-line" />
                      </Link>
                    ) : (
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold rounded-xl">
                        <i className="ri-information-line text-slate-500" />
                        Complete all {totalLessons} lessons to finish course ({completedLessonsCount}/{totalLessons})
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Quiz & Task View - Only available when hasQuiz is true */}
            {activeView === 'quiz' && hasQuiz && (
              <div className="space-y-6 animate-fade-in">
                <QuizSection
                  questions={currentLesson.quiz?.questions || []}
                  quizId={currentLesson.quiz?.id || `quiz-${currentLesson._id || currentLesson.id}`}
                  courseId={course._id}
                  lessonId={currentLesson._id || currentLesson.id}
                  isFinal={false}
                  onComplete={handleQuizComplete}
                  onCancel={() => setActiveView('content')}
                  hasNextLesson={Boolean(nextLesson)}
                  onProceedNext={handleNextLesson}
                  onBackToMaterial={() => setActiveView('content')}
                />

                {/* Post-quiz Next Steps banner */}
                <div className="flex items-center justify-between bg-white rounded-2xl border border-slate-200 p-4">
                  <button
                    type="button"
                    onClick={() => setActiveView('content')}
                    className="text-xs font-semibold text-slate-600 hover:text-teal-600 inline-flex items-center gap-1 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line" /> Back to Lesson Material
                  </button>

                  {nextLesson ? (
                    <button
                      type="button"
                      onClick={handleNextLesson}
                      disabled={!canAdvanceToNext}
                      className={`px-5 py-2.5 text-xs font-bold rounded-xl transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-sm ${
                        canAdvanceToNext 
                          ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {canAdvanceToNext ? (
                        <>Proceed to Next Lesson <i className="ri-arrow-right-line" /></>
                      ) : (
                        <><i className="ri-lock-line" /> Pass Quiz ({lessonPassingScore}%+) to Proceed</>
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleMarkComplete}
                      disabled={completing}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-sm"
                    >
                      <i className="ri-checkbox-circle-line" /> Complete Lesson
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Syllabus Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Progress Card */}
            <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
              <h3 className="font-bold text-slate-900 text-sm mb-3 flex items-center justify-between">
                <span>Course Syllabus</span>
                <span className="text-teal-600 text-xs font-mono">{progress}% Complete</span>
              </h3>
              <ProgressBar value={progress} size="md" className="mb-6" />

              {/* Course Lectures List */}
              <div className="space-y-2 max-h-[460px] overflow-y-auto pr-1">
                {(course.sections || []).map((section, sIdx) => (
                  <div key={section._id || sIdx} className="mb-4 last:mb-0">
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                      Section {sIdx + 1}: {section.title}
                    </div>
                    <div className="space-y-1.5">
                      {(section.lectures || []).map((lecture, lIdx) => {
                        const isSelected = String(lecture._id || lecture.id) === String(currentLesson._id || currentLesson.id);
                        const isDone = isLessonCompleted(course._id, lecture._id || lecture.id);
                        const hasLecQuiz = lecture.quiz?.questions?.length > 0;

                        return (
                          <button
                            key={lecture._id || lIdx}
                            type="button"
                            onClick={() => handleSelectLessonFromSidebar(lecture)}
                            className={`w-full text-left p-3 rounded-xl text-xs font-medium transition-all flex items-center justify-between gap-3 cursor-pointer ${
                              isSelected
                                ? 'bg-teal-600 text-white shadow-sm font-bold'
                                : isDone
                                ? 'bg-emerald-50 text-slate-800 hover:bg-emerald-100/70 border border-emerald-100'
                                : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-100'
                            }`}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 font-bold ${
                                isSelected 
                                  ? 'bg-white/20 text-white' 
                                  : isDone 
                                  ? 'bg-emerald-500 text-white' 
                                  : 'bg-slate-200 text-slate-600'
                              }`}>
                                {isDone ? '✓' : lIdx + 1}
                              </span>
                              <span className="truncate">{lecture.title}</span>
                            </div>

                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {lecture.type === 'slides' && <i className="ri-slideshow-line opacity-75" title="Slides" />}
                              {lecture.type === 'video' && <i className="ri-video-line opacity-75" title="Video" />}
                              {lecture.type === 'text' && <i className="ri-file-text-line opacity-75" title="Text" />}
                              {lecture.type === 'resource' && <i className="ri-file-download-line opacity-75" title="Resource" />}
                              {lecture.type === 'live-session' && <i className="ri-broadcast-line opacity-75" title="Live Session" />}
                              {lecture.type === 'audio' && <i className="ri-headphone-line opacity-75" title="Audio" />}
                              {hasLecQuiz && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${isSelected ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800 font-semibold'}`} title="Quiz Required">
                                  Quiz
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Final Quiz Card */}
            {course.finalQuiz?.questions?.length > 0 && (
              <div className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent rounded-3xl border border-amber-200/80 p-6">
                <div className="flex items-center gap-2.5 mb-2">
                  <i className="ri-trophy-fill text-amber-500 text-xl" />
                  <h4 className="font-bold text-slate-900 text-sm">Course Final Assessment</h4>
                </div>
                <p className="text-xs text-slate-600 mb-4 leading-relaxed">
                  Pass the final quiz with at least {finalPassingScore}% to unlock and download your official certificate.
                </p>
                <Link
                  to={`/courses/${course._id}`}
                  className="block text-center py-2.5 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl transition-colors shadow-sm"
                >
                  View Assessment Details
                </Link>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
